import { ProblemStage } from 'its-pages';
import { reducerWithInitialState } from 'typescript-fsa-reducers/dist';
import {
  activityStart,
  activityStop,
  userAdded,
  warnTooSmall
} from '@/redux/actions/embedding';
import { nextProblemChosen } from '@/redux/actions/progression';
import {
  duplicateInstanceDetected,
  mobileDeviceDetected,
  problemLoaded,
  setProblemLogIds,
  setStage,
  setTestPageType,
  showConfetti,
  TestPageType
} from '@/redux/actions/ui';
import { ReducerFn } from '@lib/utils/redux/reducers/types';
import { ActivityStart } from '../../utils';

export const MASTERY_THRESHOLD = 0.5;
export interface UIState {
  showingOverview: boolean;
  isActivityStarted: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  warnTooSmall: boolean;
  userId: string | null;
  classId: string;
  showConfetti: boolean;
  feedback?: JSX.Element;
  stage: ProblemStage;
  testPageType: TestPageType;
  problemLogIds: string[];
  isMobileDevice: boolean;
  /**
   * Set when another instance of the app is open in a different tab.
   * In this case, PouchDB throws an exception when accessed.
   * The only possible remedy is to either:
   * - reload the current tab
   * - continue in the tab in which the app was most recently loaded
   **/
  isDuplicateAppInstanceDetected: boolean;
}

const INITIAL_STATE: UIState = {
  isMobileDevice: false,
  warnTooSmall: false,
  isActivityStarted: false,
  isLoaded: false,
  isLoading: false,
  isDuplicateAppInstanceDetected: false,
  showingOverview: true,
  userId: null,
  classId: 'standaloneapp',
  showConfetti: false,
  stage: ProblemStage.CollectInput,
  testPageType: TestPageType.Generate,
  problemLogIds: []
};

type UIReducer<Payload = void> = ReducerFn<UIState, Payload>;

function activityStartedReducer(state: UIState, { cid }: ActivityStart): UIState {
  return {
    ...state,
    isActivityStarted: true,
    isLoaded: false,
    classId: cid
  };
}

function activityStoppedReducer(state: UIState): UIState {
  return {
    ...state,
    isActivityStarted: false
  };
}

function nextProblemChosenReducer(state: UIState): UIState {
  return { ...state, isLoaded: false, isLoading: true };
}

function problemLoadedReducer(state: UIState): UIState {
  return { ...state, isLoaded: true, isLoading: false };
}

function userAddedReducer(state: UIState, user: { userId: string }): UIState {
  return { ...state, userId: user.userId };
}

function warnTooSmallReducer(state: UIState, warnTooSmall: boolean): UIState {
  return { ...state, warnTooSmall };
}

function showConfettiReducer(state: UIState, showConfetti: boolean): UIState {
  return { ...state, showConfetti };
}

function setStageReducer(state: UIState, stage: ProblemStage): UIState {
  return { ...state, stage};
}

function setTestPageTypeReducer(state: UIState, testPageType: TestPageType): UIState {
  return { ...state, testPageType};
}

function setProblemLogIdsReducer(state: UIState, problemLogIds: string[]): UIState {
  return { ...state, problemLogIds};
}

const setIsMobileDevice = (state: UIState, payload: boolean): UIState => ({
  ...state, isMobileDevice: payload
});

const setDuplicateInstance: UIReducer = (state) => ({
  ...state, isDuplicateAppInstanceDetected: true
})

export const uiReducer = reducerWithInitialState(INITIAL_STATE)
  .case(activityStart, activityStartedReducer)
  .case(activityStop, activityStoppedReducer)
  .case(warnTooSmall, warnTooSmallReducer)
  .case(nextProblemChosen, nextProblemChosenReducer)
  .case(userAdded, userAddedReducer)
  .case(problemLoaded, problemLoadedReducer)
  .case(showConfetti, showConfettiReducer)
  .case(setStage, setStageReducer)
  .case(setTestPageType, setTestPageTypeReducer)
  .case(setProblemLogIds, setProblemLogIdsReducer)
  .case(mobileDeviceDetected, setIsMobileDevice)
  .case(duplicateInstanceDetected, setDuplicateInstance)
  .build();
